/**
 * BoardRichTextEditor — 掲示板本文用の TipTap WYSIWYG エディタ。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-3・§7-4
 *
 * documents/sectionRegistry/RichTextField.tsx を土台にした掲示板専用版。
 * - StarterKit v3 は Link / Underline を同梱するため個別 extension は追加しない
 *   （二重登録の警告を避ける。link は StarterKit オプションで設定）
 * - IME (日本語入力) composition ガードは必須移植（省くとカーソル末尾飛びが再発）
 * - Phase 3: 画像 (5MB 自動圧縮) / アップロード動画 (100MB) / YouTube 埋め込みに対応。
 *   メディアのアップロード先を board/{imgs,videos}/{postId}/ に確定させるため postId を受け取る。
 */
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold as BoldIcon,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
  Video as VideoIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { toast } from '@/components/ui/toast';
import { uploadBoardImage } from '@/lib/boardMedia';

import { BoardVideoNode } from './BoardVideoNode';
import { VideoInsertDialog } from './VideoInsertDialog';

interface Props {
  /** 現在の HTML 文字列 */
  value: string;
  /** 入力毎に呼ばれる */
  onChange: (html: string) => void;
  /** メディアのアップロード先を確定させるための投稿 ID (board/imgs/{postId}/ 等) */
  postId: string;
  placeholder?: string;
  /** 高さの目安 (px)。指定なしで最小 240px */
  minHeight?: number;
}

export function BoardRichTextEditor({
  value,
  onChange,
  postId,
  placeholder,
  minHeight = 240,
}: Props) {
  // 外部からの value 更新を反映するための ref
  const lastValueRef = useRef(value);
  // IME (日本語入力) 中フラグ。composition 中は外部からの setContent を
  // スキップしてカーソル末尾飛びを防ぐ。
  const composingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // StarterKit v3 同梱の Link をオプションで設定（個別 extension の二重登録を避ける）
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      // 画像: <img src alt>。inline:false で block 要素として扱う。
      Image.configure({ inline: false }),
      // アップロード動画: <video controls src> のカスタム Node。
      BoardVideoNode,
      // YouTube: <div data-youtube-video><iframe .../></div>。
      Youtube.configure({
        controls: true,
        nocookie: false,
        width: 640,
        height: 360,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? '本文を入力...',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none px-3 py-2 leading-relaxed',
        style: `min-height: ${minHeight}px`,
      },
      handleDOMEvents: {
        compositionstart: () => {
          composingRef.current = true;
          return false;
        },
        compositionend: () => {
          composingRef.current = false;
          return false;
        },
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastValueRef.current = html;
      onChange(html);
    },
  });

  // 外部 value 変更 (編集画面の初期ロードなど) を取り込む。
  // 同一値なら no-op。IME 中もスキップ。
  useEffect(() => {
    if (!editor) return;
    if (composingRef.current) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  return (
    <div className="border border-[var(--border)] rounded-md bg-[var(--bg-input)] overflow-hidden">
      {editor && <Toolbar editor={editor} postId={postId} />}
      <div className="border-t border-[var(--border)]">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor, postId }: { editor: Editor; postId: string }) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  const handleImageFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('画像ファイルを選択してください');
      return;
    }
    setImageUploading(true);
    try {
      const url = await uploadBoardImage(postId, file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      toast.error(
        `画像のアップロードに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setImageUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const btn = (active: boolean) =>
    `px-1.5 py-1 rounded-sm text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors ${
      active ? 'bg-[var(--accent-subtle)] text-[var(--accent)]' : ''
    }`;

  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL を入力:', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 bg-[var(--bg-elevated)] flex-wrap">
      <button
        type="button"
        title="見出し H2"
        className={btn(editor.isActive('heading', { level: 2 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-3.5" />
      </button>
      <button
        type="button"
        title="見出し H3"
        className={btn(editor.isActive('heading', { level: 3 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="太字 (Cmd+B)"
        className={btn(editor.isActive('bold'))}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="斜体 (Cmd+I)"
        className={btn(editor.isActive('italic'))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="下線 (Cmd+U)"
        className={btn(editor.isActive('underline'))}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="取り消し線"
        className={btn(editor.isActive('strike'))}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="箇条書き"
        className={btn(editor.isActive('bulletList'))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-3.5" />
      </button>
      <button
        type="button"
        title="番号付きリスト"
        className={btn(editor.isActive('orderedList'))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-3.5" />
      </button>
      <button
        type="button"
        title="引用"
        className={btn(editor.isActive('blockquote'))}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      <button
        type="button"
        title="リンク"
        className={btn(editor.isActive('link'))}
        onClick={setLink}
      >
        <LinkIcon className="size-3.5" />
      </button>
      <span className="w-px h-4 bg-[var(--border)] mx-1" />
      {/* 画像挿入 */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleImageFile(f);
        }}
      />
      <button
        type="button"
        title="画像を挿入"
        className={btn(false)}
        disabled={imageUploading}
        onClick={() => imageInputRef.current?.click()}
      >
        <ImageIcon className="size-3.5" />
      </button>
      {/* 動画挿入 (アップロード / YouTube) */}
      <button
        type="button"
        title="動画を挿入"
        className={btn(false)}
        onClick={() => setVideoDialogOpen(true)}
      >
        <VideoIcon className="size-3.5" />
      </button>
      {imageUploading && (
        <span className="text-xs text-[var(--text-muted)] ml-1">
          画像アップロード中...
        </span>
      )}
      <VideoInsertDialog
        postId={postId}
        editor={editor}
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
      />
      <span className="flex-1" />
      <button
        type="button"
        title="元に戻す (Cmd+Z)"
        className={btn(false)}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="size-3.5" />
      </button>
      <button
        type="button"
        title="やり直し (Cmd+Shift+Z)"
        className={btn(false)}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="size-3.5" />
      </button>
    </div>
  );
}
